/* eslint-disable @typescript-eslint/unbound-method */
// Disabling the linting on this file due to a known issue: https://github.com/typescript-eslint/typescript-eslint/issues/1929
import { Component, OnInit, computed, inject, input, output, signal } from '@angular/core'
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { LogService } from 'src/app/core/services/log.service'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatIconModule } from '@angular/material/icon'
import { CommonModule, formatCurrency } from '@angular/common'
import { ButtonModule } from '../button/button.module'
import { NgxMaskDirective } from 'ngx-mask'
import { I18nService } from 'src/app/core/services/i18n.service'
import { I18nText } from 'src/app/core/interfaces/i18n.service'
import { FormService } from 'src/app/core/services/form.service'
import { ToastService } from 'src/app/core/services/toast.service'
import { GlobalDataService } from 'src/app/core/services/global-data.service'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { FormAutofillService } from 'src/app/core/services/form-autofill.service'
import { ColorSchemeDirective } from 'src/app/core/directives/color-scheme.directive'


@Component({
  selector: 'app-contact-form',
  templateUrl: './contact-form.component.html',
  styleUrls: ['./contact-form.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatInputModule,
    MatCheckboxModule,
    ButtonModule,
    NgxMaskDirective,
    ColorSchemeDirective,
  ],
  standalone: true,
})
export class ContactFormComponent implements OnInit {
  private _logger = inject(LogService)
  private _formService = inject(FormService)
  private _toastService = inject(ToastService)
  private _formAutofillService = inject(FormAutofillService)
  protected globalData = inject(GlobalDataService)
  protected i18n = inject(I18nService)

  readonly MAX_OFFER_PRICE = 999999999999999

  submitSuccess = output<void>()
  submitError = output<string>()
  closeForm = output<void>()

  withCloseButton = input(false)
  hideSubmitButton = input(false)
  type = input<'asset' | 'contact'>('contact')
  additionalData = input<Record<string, any>>({})
  successTitle = input<I18nText | string>('Thank You!')
  successBody = input<I18nText | string>('We have received your submission and will be in touch soon!')

  acknowledgementEnabled = input(false)
  customAcknowledgementText = input('')

  contactForm: FormGroup = new FormGroup({})
  disabled: boolean = true
  submitStatus: 'idle' | 'submitting' | 'success' | 'error' = 'idle'

  offerHint = $localize`:@@contact-form.input.my-offer.hint:Offer in __CURRENCY__`
  offerHintComputed = computed(() => {
    const currency = this.globalData.selectedCurrency().code
    return this.offerHint.replace('__CURRENCY__', currency)
  })
  offerDisplay = signal('')

  ngOnInit(): void {
    const {
      firstName = '',
      lastName = '',
      email = '',
      phone = '',
      companyName = '',
    } = this._formAutofillService.getContactDetails() || {}
    if (this._formService.isRecaptchaLoaded()) {
      this.disabled = false
    }
    this.contactForm.addControl('first_name', new FormControl({ value: firstName, disabled: this.disabled }, [
      Validators.required,
      Validators.maxLength(50)
    ]))
    this.contactForm.addControl('last_name', new FormControl({ value: lastName, disabled: this.disabled }, [
      Validators.required,
      Validators.maxLength(50)
    ]))
    this.contactForm.addControl('company_name', new FormControl({ value: companyName, disabled: this.disabled }, [
      Validators.required,
      Validators.maxLength(50)
    ]))
    this.contactForm.addControl('email', new FormControl({ value: email, disabled: this.disabled }, [
      Validators.required,
      Validators.email,
      Validators.maxLength(50)
    ]))
    this.contactForm.addControl('phone', new FormControl({ value: phone, disabled: this.disabled }, [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(16)
    ]))
    this.contactForm.addControl('message', new FormControl({ value: '', disabled: this.disabled }, [
      Validators.maxLength(1500)
    ]))
    if (this.type() === 'asset') {
      this.contactForm.addControl('offer_price_native', new FormControl({ value: null, disabled: this.disabled }, [
        Validators.max(this.MAX_OFFER_PRICE)
      ]))
      this.contactForm.addControl('offer_currency', new FormControl({ value: this.globalData.selectedCurrency().code, disabled: this.disabled }, []))
    }
    if (this.acknowledgementEnabled()) {
      this.contactForm.addControl('acknowledgement_agreement', new FormControl({ value: false, disabled: this.disabled }, []))
    }
  }

  get firstName() { return this.contactForm.get('first_name') }
  get lastName() { return this.contactForm.get('last_name') }
  get company() { return this.contactForm.get('company_name') }
  get offer() { return this.contactForm.get('offer_price_native') }
  get email() { return this.contactForm.get('email') }
  get phone() { return this.contactForm.get('phone') }
  get message() { return this.contactForm.get('message') }
  get acknowledgement_agreement() { return this.contactForm.get('acknowledgement_agreement') }

  onOfferKeydown(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab']
    const tryingToPaste = (event.ctrlKey || event.metaKey) && event.code === 'KeyV'

    // Check if the key is a number or an allowed key / key combo
    if (
      !/^\d$/.test(event.key)
      && !allowedKeys.includes(event.key)
      && !tryingToPaste
    ) {
      event.stopPropagation()
      event.preventDefault()
      return
    }

    const inputEl = event.target as HTMLInputElement
    const cursorPosition = inputEl.selectionEnd // Get the cursor position before the key press

    if (cursorPosition === null || tryingToPaste) {
      return
    }

    // offerDisplay will be updated in onOfferInput which will happen after this method completes.
    // At that point we need to adjust the cursor position because the mask will always replace
    // the input value with a formatted value, pushing the cursor to the end
    const offerDisplayBeforeUpdate = this.offerDisplay()
    setTimeout(() => {
      const displayLengthDiff = this.offerDisplay().length - offerDisplayBeforeUpdate.length
      if (displayLengthDiff === 0) {
        if (['Backspace', 'Delete'].includes(event.key)) {
          // put the cursor back where it was
          // this happens if the user backspaces or deletes on a non-number character
          inputEl.setSelectionRange(cursorPosition, cursorPosition)
        }
        return
      }
      // length changed, figure out where the cursor should be
      if (['Delete'].includes(event.key)) {
        inputEl.setSelectionRange(cursorPosition, cursorPosition)
        return
      }
      let nextPosition = cursorPosition + displayLengthDiff
      if (nextPosition <= 0) {
        nextPosition = 1
      }
      inputEl.setSelectionRange(nextPosition, nextPosition)
    }, 0)
  }

  onOfferInput(event: Event): void {
    const inputEl = event.target as HTMLInputElement
    const {
      symbol,
      code,
    } = this.globalData.selectedCurrency()
    const inputVal = inputEl.value.trim()
    const rawValue = inputVal.replace(new RegExp(`[^0-9]`, 'g'), '')
    if (!rawValue || isNaN(Number(rawValue)) || Number(rawValue) === 0) {
      this.contactForm.get('offer_price_native')?.setValue(null)
      this.offerDisplay.set('')
      inputEl.value = ''
      return
    }
    const numericValue = Number(rawValue)
    const locale = navigator.language || 'en-US'
    const formattedValue = formatCurrency(numericValue, locale, symbol, code, '1.0-0')
    this.contactForm.get('offer_price_native')?.setValue(numericValue)
    this.offerDisplay.set(formattedValue)
    inputEl.value = formattedValue
  }

  submit(): void {
    Object.values(this.contactForm.controls).forEach(control => {
      control.markAsTouched() // causes form to validate
    })
    if (this.contactForm.invalid) { return }
    this._logger.debug('submitting contact form: ', this.contactForm.value)
    this.submitStatus = 'submitting'
    const formData = {
      ...this.contactForm.value,
      ...this.additionalData(),
      form_type: this.type(),
      page_url: window.location.href,
    }
    if (this.acknowledgementEnabled()) {
      formData.acknowledgement_agreement_label = this.i18n.get(this.customAcknowledgementText())
    }
    this._formService.submitContactForm(formData).subscribe((response) => {
      if (response.success) {
        this._formAutofillService.saveContactDetails({
          firstName: this.firstName?.value,
          lastName: this.lastName?.value,
          email: this.email?.value,
          phone: this.phone?.value,
          companyName: this.company?.value,
        })
        this.contactForm.reset()
        this._logger.debug('contact form submitted successfully')
        this.submitSuccess.emit()
        this.submitStatus = 'success'
      } else {
        this._toastService.error({
          message: 'There was an error submitting your form. Please try again.',
          includeClose: true,
        })
        this._logger.error(`action: submitContactForm, response_status: ${response.success}, response_message: ${response.reason}`)
        this.submitError.emit(response.reason)
        this.submitStatus = 'error'
      }
    })
  }
}
